import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, setDoc, serverTimestamp, getDoc, db } from '../firebase.config';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import imageCompression from 'browser-image-compression';
import { companyTypes } from '../utils/companyUtils';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { EmployeeManager } from '../utils/enhancedFirebaseUtils';
import { saveCompanyDetails } from '../utils/dataAdapter';
import CurrencySettings from './CurrencySettings';
import CompanyWizard from './CompanyWizard/CompanyWizard';
import UsageMonitor from './UsageMonitor';
import DeleteCompanyModal from './DeleteCompanyModal';
import { deleteCompanyCompletely, canDeleteCompany } from '../utils/companyDeletion';
import { useNotification } from '../hooks/useNotification';
import { ModernInfoCard, InfoRow, ImagePreview, QuickActionButton } from './CompanyDetailsModern';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';
import {
  Building2,
  MapPin,
  Landmark,
  FileText as FileTextIcon,
  Settings as SettingsIcon,
  Mail,
  Phone as PhoneIcon,
  CreditCard,
  Users,
  Receipt,
  Award,
  Hash,
  Globe
} from 'lucide-react';

const auth = getAuth();

const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

// Indian PAN validation regex (5 letters + 4 digits + 1 letter)
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const stateCodeMap = {
    'Andhra Pradesh': '37', 'Arunachal Pradesh': '12', 'Assam': '18', 'Bihar': '10', 'Chhattisgarh': '22',
    'Goa': '30', 'Gujarat': '24', 'Haryana': '06', 'Himachal Pradesh': '02', 'Jharkhand': '20',
    'Karnataka': '29', 'Kerala': '32', 'Madhya Pradesh': '23', 'Maharashtra': '27', 'Manipur': '14',
    'Meghalaya': '17', 'Mizoram': '15', 'Nagaland': '13', 'Odisha': '21', 'Punjab': '03',
    'Rajasthan': '08', 'Sikkim': '11', 'Tamil Nadu': '33', 'Telangana': '36', 'Tripura': '16',
    'Uttar Pradesh': '09', 'Uttarakhand': '05', 'West Bengal': '19',
    'Andaman and Nicobar Islands': '35', 'Chandigarh': '04', 'Dadra and Nagar Haveli and Daman and Diu': '26',
    'Delhi': '07', 'Jammu and Kashmir': '01', 'Ladakh': '38', 'Lakshadweep': '31', 'Puducherry': '34'
};

const CompanyDetails = ({ db, userId, isAuthReady, setActiveModule, appId, onOpenWizard, refreshCompanyDetails, refreshTrigger }) => {
    const { currentCompany, employees, loadCompanyEmployees, createCompany } = useEnhancedCompany();
    const location = useLocation();
    const navigate = useNavigate();
    const notify = useNotification();
    
    // Wizard modal state
    const [showWizard, setShowWizard] = useState(false);
    const [wizardMode, setWizardMode] = useState('create'); // 'create' or 'edit'
    const [wizardData, setWizardData] = useState({});
    const [wizardStartStep, setWizardStartStep] = useState(0);
    
    // Delete company modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    
    // Tab state for company details
    const [activeTab, setActiveTab] = useState('profile');
    
    // Check URL parameters for wizard
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const wizardParam = urlParams.get('wizard');
        console.log('🔍 Checking wizard param:', wizardParam);
        if (wizardParam === 'create') {
            console.log('🚀 Opening wizard in create mode');
            openWizard({}, 'create');
            // Remove the parameter from URL
            const newUrl = new URL(window.location);
            newUrl.searchParams.delete('wizard');
            window.history.replaceState({}, '', newUrl);
            console.log('✅ Wizard should now be open, showWizard:', true);
        }
    }, [location.search]);
    const [firmName, setFirmName] = useState('');
    const [gstin, setGstin] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [pincode, setPincode] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [email, setEmail] = useState('');
    const [pan, setPan] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [businessType, setBusinessType] = useState('');
    const [gstinType, setGstinType] = useState('');
    const [gstinTypeWarning, setGstinTypeWarning] = useState('');
    const [gstinStateError, setGstinStateError] = useState('');
    const prevGstinType = useRef('');
    const [companyType, setCompanyType] = useState('proprietorship');
    const [bankName, setBankName] = useState('');
    const [bankAccount, setBankAccount] = useState('');
    const [bankIfsc, setBankIfsc] = useState('');
    const [upiId, setUpiId] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [signUrl, setSignUrl] = useState('');
    const [sealUrl, setSealUrl] = useState('');
    const [upiQrUrl, setUpiQrUrl] = useState('');
    const [message, setMessage] = useState('');
    const [section, setSection] = useState(() => {
        const p = new URLSearchParams(location.search).get('section');
        return p || 'profile';
    });
    const [logoPreview, setLogoPreview] = useState('');
    const [signPreview, setSignPreview] = useState('');
    const [sealPreview, setSealPreview] = useState('');
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoProgress, setLogoProgress] = useState(0);
    const [signUploading, setSignUploading] = useState(false);
    const [signProgress, setSignProgress] = useState(0);
    const [sealUploading, setSealUploading] = useState(false);
    const [sealProgress, setSealProgress] = useState(0);
    // Add new state variables
    const [upiQrPreview, setUpiQrPreview] = useState('');
    const [upiQrUploading, setUpiQrUploading] = useState(false);
    const [upiQrProgress, setUpiQrProgress] = useState(0);
    const [paymentGatewayLink, setPaymentGatewayLink] = useState('');
    // Settings & plan
    const [currency, setCurrency] = useState('INR');
    const [gstCalculation, setGstCalculation] = useState('excluding');
    const [selectedPlan, setSelectedPlan] = useState('');
    
    // Firebase operations tracking state
    const [firebaseUsageLoading, setFirebaseUsageLoading] = useState(false);
    // Add state at the top:
    const [salesTerms, setSalesTerms] = useState('');
    const [purchaseTerms, setPurchaseTerms] = useState('');
    const [footer, setFooter] = useState('');
    const [quotationTerms, setQuotationTerms] = useState('');
    const [companyTypeOther, setCompanyTypeOther] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [companyRole, setCompanyRole] = useState('');

    // Use employees from enhanced context
    const employeesList = employees || [];
    // Employee join status tracking
    const [employeeJoinStatus, setEmployeeJoinStatus] = useState({});
    
    // Wizard functions
    const getCurrentDetailsSnapshot = () => ({
        // Basic Info
        firmName,
        companyType,
        gstinType,
        gstin,
        email,
        contactNumber,
        pan,
        
        // Address
        address,
        city,
        state,
        pincode,
        country: 'India',
        
        // Bank Details
        bankName,
        bankAccount,
        bankIfsc,
        upiId,
        upiQrUrl,
        
        // Documents/Images
        logoUrl,
        sealUrl,
        signUrl,
        
        // Company Settings
        contactPerson,
        businessType,
        currency,
        gstCalculation,
        
        // Terms & Settings
        salesTerms,
        purchaseTerms,
        quotationTerms,
        footer,
        
        // Subscription
        selectedPlan,
    });

    const openWizard = (data = {}, mode = 'create', startStep = 0) => {
        // Always prefill wizard with FULL current details, then override with section data
        const currentSnapshot = getCurrentDetailsSnapshot();
        const prefills = { ...currentSnapshot, ...data };
        setWizardData(prefills);
        setWizardMode(mode);
        setWizardStartStep(startStep);
        setShowWizard(true);
    };
    
    const closeWizard = () => {
        setShowWizard(false);
        setWizardData({});
        setWizardMode('create');
        setWizardStartStep(0);
    };

    // Terms Dropdown Functions
    const toggleTermsDropdown = (termType) => {
        setActiveTermsDropdown(activeTermsDropdown === termType ? '' : termType);
    };

    const handleTermsEdit = () => {
        openWizard({ salesTerms, purchaseTerms, quotationTerms, footer }, 'edit', 6);
        setActiveTermsDropdown(''); // Close dropdown after action
    };

    // Delete Company Functions
    const checkDeletePermissions = async () => {
        if (!db || !companyId || !userId) return;
        
        const result = await canDeleteCompany(db, companyId, userId);
        setCanDelete(result.canDelete);
        
        if (!result.canDelete) {
            // Delete not allowed - reason is available in result.reason
        }
    };

    const handleDeleteCompany = async (companyIdToDelete) => {
        try {
            const result = await deleteCompanyCompletely(db, companyIdToDelete, userId);
            
            if (result.success) {
                // Show success notification
                notify.success(
                    `Company deleted successfully!\n\n${result.stats.subcollectionsDeleted} subcollections and ${result.stats.documentsDeleted} documents removed.`,
                    { title: 'Deletion Complete', duration: 6000 }
                );
                
                // Redirect to dashboard or companies list
                setTimeout(() => {
                    navigate('/');
                    window.location.reload();
                }, 1500);
            }
            
            return result;
        } catch (error) {
            console.error('Error deleting company:', error);
            return {
                success: false,
                error: error.message
            };
        }
    };

    // Check delete permissions when component mounts
    useEffect(() => {
        checkDeletePermissions();
    }, [db, companyId, userId]);

    
    const handleWizardComplete = async (companyData) => {
        try {
            if (wizardMode === 'create') {
                // Create new company using unified logic
                const result = await createCompany({
                    name: companyData.firmName || companyData.ownerName,
                    firmName: companyData.firmName || companyData.ownerName,
                    type: companyData.companyType,
                    gstType: companyData.gstinType,
                    gstNumber: companyData.gstin,
                    email: companyData.email,
                    phone: companyData.contactNumber,
                    pan: companyData.pan,
                    address: companyData.address,
                    city: companyData.city,
                    state: companyData.state,
                    pincode: companyData.pincode,
                    country: companyData.country,
                    bankName: companyData.bankName,
                    bankAccount: companyData.bankAccount,
                    bankIfsc: companyData.bankIfsc,
                    upiId: companyData.upiId,
                    upiQrUrl: companyData.upiQrUrl,
                    logoUrl: companyData.logoUrl,
                    sealUrl: companyData.sealUrl,
                    signUrl: companyData.signUrl,
                    currency: companyData.currency,
                    gstCalculation: companyData.gstCalculation,
                    salesTerms: companyData.salesTerms,
                    purchaseTerms: companyData.purchaseTerms,
                    quotationTerms: companyData.quotationTerms,
                    footer: companyData.footer,
                    selectedPlan: companyData.selectedPlan,
                    businessType: companyData.businessType,
                    contactPerson: companyData.contactPerson,
                    details: companyData
                });
                
                if (result.success) {
                    notify.success('Company created successfully!', { 
                        title: 'Company Created',
                        details: `${companyData.firmName || companyData.name || 'Company'} • ${companyData.gstinType || 'No GST type'}`,
                        duration: 6000 
                    });
                    closeWizard();
                    // Refresh company details
                    if (refreshCompanyDetails) {
                        refreshCompanyDetails();
                    }
                } else {
                    notify.error(`Error: ${result.error}`, { title: 'Creation Failed', duration: 7000 });
                }
            } else {
                // Update existing company
                const cid = currentCompany?.companyId || currentCompany?.id;
                if (cid) {
                    // Map wizard fields to actual Firestore schema (root-level + nested branding/preferences)
                    const companyRef = doc(db, 'companies', cid);
                    const updatePayload = {};

                    // System fields (keep at root level for easy access)
                    updatePayload.name = companyData.firmName || '';
                    
                    // Basic company info organized in map
                    updatePayload.basicInfo = {
                        businessType: companyData.businessType || '',
                        companyType: companyData.companyType || '',
                        contactPerson: companyData.contactPerson || '',
                        email: companyData.email || '',
                        phone: companyData.contactNumber || '',
                        pan: companyData.pan || '',
                        gstNumber: companyData.gstin || '',
                        gstType: companyData.gstinType || '',
                        currency: companyData.currency || 'INR'
                    };

                    // Categorized data using maps
                    updatePayload.details = {
                        address: companyData.address || '',
                        city: companyData.city || '',
                        state: companyData.state || '',
                        pincode: companyData.pincode || '',
                        country: companyData.country || 'India'
                    };

                    updatePayload.bankDetails = {
                        bankName: companyData.bankName || '',
                        bankAccount: companyData.bankAccount || '',
                        bankIfsc: companyData.bankIfsc || '',
                        upiId: companyData.upiId || '',
                        upiQrUrl: companyData.upiQrUrl || ''
                    };

                    updatePayload.branding = {
                        logoUrl: companyData.logoUrl || '',
                        sealUrl: companyData.sealUrl || '',
                        signatureUrl: companyData.signUrl || ''
                    };

                    updatePayload.termsAndConditions = {
                        footer: companyData.footer || '',
                        salesTerms: companyData.salesTerms || '',
                        purchaseTerms: companyData.purchaseTerms || '',
                        quotationTerms: companyData.quotationTerms || ''
                    };

                    updatePayload.preferences = {
                        defaultCurrency: companyData.currency || 'INR',
                        gstCalculation: companyData.gstCalculation || 'excluding',
                        selectedPlan: companyData.selectedPlan || 'basic'
                    };

                    await setDoc(companyRef, updatePayload, { merge: true });
                    
                    // Company data is now stored only in companies collection
                    
                    // Force immediate UI update by setting state variables directly
                    setFirmName(companyData.firmName || '');
                    setCompanyType(companyData.companyType || '');
                    setBusinessType(companyData.businessType || '');
                    setContactPerson(companyData.contactPerson || '');
                    setEmail(companyData.email || '');
                    setContactNumber(companyData.contactNumber || '');
                    setPan(companyData.pan || '');
                    setGstinType(companyData.gstinType || '');
                    setGstin(companyData.gstin || '');
                    setAddress(companyData.address || '');
                    setCity(companyData.city || '');
                    setState(companyData.state || '');
                    setPincode(companyData.pincode || '');
                    setBankName(companyData.bankName || '');
                    setBankAccount(companyData.bankAccount || '');
                    setBankIfsc(companyData.bankIfsc || '');
                    setUpiId(companyData.upiId || '');
                    setUpiQrUrl(companyData.upiQrUrl || '');
                    setLogoUrl(companyData.logoUrl || '');
                    setSealUrl(companyData.sealUrl || '');
                    setSignUrl(companyData.signUrl || '');
                    setSalesTerms(companyData.salesTerms || '');
                    setPurchaseTerms(companyData.purchaseTerms || '');
                    setQuotationTerms(companyData.quotationTerms || '');
                    setFooter(companyData.footer || '');
                    setCurrency(companyData.currency || 'INR');
                    setGstCalculation(companyData.gstCalculation || 'excluding');
                    setSelectedPlan(companyData.selectedPlan || 'basic');
                    
                    // Update user_companies with new company name and type
                    try {
                        const userCompaniesRef = doc(db, `user_companies/${auth.currentUser.uid}`);
                        const userCompaniesSnap = await getDoc(userCompaniesRef);
                        
                        if (userCompaniesSnap.exists()) {
                            const userData = userCompaniesSnap.data();
                            
                            // Update owned_companies array
                            const updatedOwnedCompanies = (userData.owned_companies || []).map(company => {
                                if (company.companyId === cid) {
                                    return {
                                        ...company,
                                        name: companyData.firmName || company.name || '',
                                        type: companyData.companyType || company.type || '',
                                        businessType: companyData.businessType || company.businessType || ''
                                    };
                                }
                                return company;
                            });
                            
                            // Update companies array
                            const updatedCompanies = (userData.companies || []).map(company => {
                                if (company.companyId === cid) {
                                    return {
                                        ...company,
                                        name: companyData.firmName || company.name || '',
                                        type: companyData.companyType || company.type || '',
                                        businessType: companyData.businessType || company.businessType || ''
                                    };
                                }
                                return company;
                            });
                            
                            // Update active_company if it's the current company
                            let updatedActiveCompany = userData.active_company;
                            if (userData.active_company?.companyId === cid) {
                                updatedActiveCompany = {
                                    ...userData.active_company,
                                    name: companyData.firmName || userData.active_company.name || ''
                                };
                            }
                            
                            // Prepare update data with no undefined fields
                            const updateData = {
                                owned_companies: updatedOwnedCompanies,
                                companies: updatedCompanies,
                                active_company: updatedActiveCompany,
                                last_updated: new Date()
                            };
                            
                            // Filter out undefined values
                            const cleanUpdateData = {};
                            Object.keys(updateData).forEach(key => {
                                const value = updateData[key];
                                if (value !== undefined) {
                                    cleanUpdateData[key] = value;
                                }
                            });
                            
                            await setDoc(userCompaniesRef, cleanUpdateData, { merge: true });
                            
                        }
                    } catch (error) {
                        console.error('⚠️ Failed to update user_companies:', error);
                    }
                    
                    // Also save currency to settings/default for CurrencySettings component
                    if (companyData.currency) {
                        const settingsRef = doc(db, `companies/${cid}/settings/default`);
                        await setDoc(settingsRef, {
                            currency: companyData.currency,
                            availableCurrencies: [companyData.currency],
                            updatedAt: new Date()
                        }, { merge: true });
                    }
                    
                    
                    notify.success('Company updated successfully!', { 
                        title: 'Company Updated',
                        details: `${companyData.firmName || companyData.name || 'Company'} • ${companyData.gstinType || 'No GST type'}`,
                        duration: 6000 
                    });
                    closeWizard();
                    // Refresh company details
                    if (refreshCompanyDetails) {
                        refreshCompanyDetails();
                    }
                }
            }
        } catch (err) {
            console.error('Error in wizard completion:', err);
            notify.error(`Error: ${err.message}`, { title: 'Operation Failed', duration: 7000 });
        }
    };
    
    // Payment Gateway Settings
    const [pg, setPg] = useState({ provider: 'razorpay', mode: 'test', keyId: '' });
    const [pgMsg, setPgMsg] = useState('');
    
    // SMS Settings
    const [sms, setSms] = useState({ provider: 'msg91', senderId: '', defaultTemplate: '' });
    const [smsMsg, setSmsMsg] = useState('');
    
    // Branding Settings
    const [brand, setBrand] = useState({ shareMessageDefault: '', footerText: '' });
    const [brandMsg, setBrandMsg] = useState('');
    
    // Assignment UI
    const pageStructure = {
        'Business': {
            'Sales': ['Invoice', 'Challan', 'Quotation'],
            'Purchases': ['Purchase Bill', 'Purchase Order'],
            'Payments': ['Invoice', 'Challan', 'Purchase', 'Payment Receipts', 'Payment Mode'],
            'Expenses': ['Fixed', 'Variable', 'Labour/Advances', 'Employees', 'Salary'],
            'Manufacturing': ['Production Orders', 'Process Definitions', 'Process Directory', 'Analytics']
        },
        'Management': {
            'Items': [],
            'Parties': ['Parties Information', 'Parties Outstandings', 'Opening Balance Management'],
            'Bill Templates': []
        },
        'Reports': {
            'Business Reports': ['Partywise Sales Report', 'Customer Ledger Report', 'Invoice Collection Report', 'Payment Register Report', 'Aging Report', 'Itemwise Sales Report', 'Purchase Bills Summary', 'Stock Report'],
            'Tax Reports': ['GST Summary Report', 'GSTR-1 Report', 'GSTR-3B Report', 'HSN-wise GST Summary', 'Tax Payments Report'],
            'Financial Reports': ['Profit & Loss Report', 'Balance Sheet Report', 'Cash Flow Report'],
            'Data Export': ['Data Export Tools']
        },
        'System': {
            'Settings': ['Profile', 'Security & MPIN (User-oriented)', 'MPIN Access Control', 'Payments', 'SMS', 'Branding'],
            'Company': ['Company Details', 'Company Settings (Company-oriented MPIN)'],
            'Help & Support': ['Help', 'FAQ', 'Contact Support'],
            'Image Manager': ['Image Management']
        }
    };
    const [assignmentType, setAssignmentType] = useState('employee'); // 'employee' | 'role'
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [selectedRole, setSelectedRole] = useState('manager');
    const [assignment, setAssignment] = useState({});
    const [assignmentMsg, setAssignmentMsg] = useState('');
    
    // Employee Status & Invites
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [employeeInvites, setEmployeeInvites] = useState([]);
    const [inviteStats, setInviteStats] = useState({});
    const [loadingInvites, setLoadingInvites] = useState(false);
    
    // Employee Table Filters
    const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
    const [employeeDepartmentFilter, setEmployeeDepartmentFilter] = useState('');
    const [employeePostFilter, setEmployeePostFilter] = useState('');
    const [selectedEmployeeForInvite, setSelectedEmployeeForInvite] = useState(null);
    
    // Terms Dropdown State
    const [activeTermsDropdown, setActiveTermsDropdown] = useState('');

    // Handle ESC key press and click outside for terms dropdown
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'Escape' && activeTermsDropdown) {
                setActiveTermsDropdown('');
            }
        };

        const handleClickOutside = () => {
            setActiveTermsDropdown('');
        };

        if (activeTermsDropdown) {
            document.addEventListener('keydown', handleKeyPress);
            document.addEventListener('click', handleClickOutside);
            return () => {
                document.removeEventListener('keydown', handleKeyPress);
                document.removeEventListener('click', handleClickOutside);
            };
        }
    }, [activeTermsDropdown]);

    useEffect(() => {
        const auth = getAuth();
        const cid = currentCompany?.companyId || currentCompany?.id || '';
        setCompanyId(cid);
        // Guard: only attach realtime listener when using real Firestore (not mock)
        if (!db || !isAuthReady || !cid) {
            return;
        }
        if (db && isAuthReady && cid) {
            
            // Prefer realtime updates on new schema root doc
            const unsub = onSnapshot(doc(db, 'companies', cid), (snap) => {
                if (!snap.exists()) {
                    return;
                }
                
                const rootData = snap.data() || {};
                
                // Extract data from new organized structure with fallback to old structure
                const basicInfo = rootData.basicInfo || {};
                const details = rootData.details || {};
                const bankDetails = rootData.bankDetails || {};
                const branding = rootData.branding || {};
                const termsAndConditions = rootData.termsAndConditions || {};
                const preferences = rootData.preferences || {};
                
                // Basic company information from basicInfo map with fallback to root level
                setFirmName(rootData.name || '');
                setGstin(basicInfo.gstNumber || rootData.gstNumber || '');
                setAddress(details.address || rootData.address || '');
                setCity(details.city || rootData.city || '');
                setState(details.state || rootData.state || '');
                setPincode(details.pincode || rootData.pincode || '');
                setContactNumber(basicInfo.phone || rootData.phone || '');
                setEmail(basicInfo.email || rootData.email || '');
                setPan(basicInfo.pan || rootData.pan || '');
                setGstinType(basicInfo.gstType || rootData.gstType || '');
                setContactPerson(basicInfo.contactPerson || rootData.contactPerson || '');
                setBusinessType(basicInfo.businessType || rootData.businessType || '');
                
                // Banking details from bankDetails map with fallback to root level
                setBankName(bankDetails.bankName || rootData.bankName || '');
                setBankAccount(bankDetails.bankAccount || rootData.bankAccount || '');
                setBankIfsc(bankDetails.bankIfsc || rootData.bankIfsc || '');
                setUpiId(bankDetails.upiId || rootData.upiId || '');
                setUpiQrUrl(bankDetails.upiQrUrl || rootData.upiQrUrl || '');
                setPaymentGatewayLink(bankDetails.paymentGatewayLink || rootData.paymentGatewayLink || '');
                
                // Images/documents from branding map with fallback to root level
                setLogoUrl(branding.logoUrl || rootData.logoUrl || '');
                setSignUrl(branding.signatureUrl || rootData.signatureUrl || rootData.signUrl || '');
                setSealUrl(branding.sealUrl || rootData.sealUrl || '');
                
                // Terms and settings from termsAndConditions map with fallback to root level
                setSalesTerms(termsAndConditions.salesTerms || rootData.salesTerms || '');
                setPurchaseTerms(termsAndConditions.purchaseTerms || rootData.purchaseTerms || '');
                setFooter(termsAndConditions.footer || rootData.footer || '');
                setQuotationTerms(termsAndConditions.quotationTerms || rootData.quotationTerms || '');
                setCompanyType(basicInfo.companyType || rootData.companyType || 'proprietorship');
                setCompanyTypeOther(basicInfo.companyTypeOther || rootData.companyTypeOther || '');
                setCompanyRole(currentCompany?.type === 'owned' ? 'owner' : (currentCompany?.type || ''));
                // Settings & plan from preferences map with fallback to root level
                setCurrency(preferences.defaultCurrency || basicInfo.currency || rootData.currency || 'INR');
                setGstCalculation(preferences.gstCalculation || rootData.gstCalculation || 'excluding');
                setSelectedPlan(preferences.selectedPlan || rootData.selectedPlan || rootData.plan || 'basic');
                
                // Also load subscription data from subscription_details path (backoffice sync)
                loadSubscriptionData(cid);
            });
            return () => { try { unsub(); } catch (_) {} };
        }
    }, [db, isAuthReady, currentCompany, refreshTrigger]);

    // Update wizard data when company data changes (for edit mode)
    useEffect(() => {
        if (showWizard && wizardMode === 'edit') {
            const currentSnapshot = getCurrentDetailsSnapshot();
            setWizardData(prev => ({ ...prev, ...currentSnapshot }));
        }
    }, [firmName, gstin, address, city, state, pincode, contactNumber, email, pan, gstinType, 
        bankName, bankAccount, bankIfsc, upiId, upiQrUrl, logoUrl, sealUrl, signUrl, 
        salesTerms, purchaseTerms, quotationTerms, footer, companyType, businessType, 
        contactPerson, currency, gstCalculation, selectedPlan, showWizard, wizardMode]);

    // Load assignment document
    const loadAssignment = async () => {
        try {
            if (!db || !companyId) return;
            setAssignmentMsg('');
            let ref;
            if (assignmentType === 'employee' && selectedEmployee) {
                ref = doc(db, `companies/${companyId}/employee_assignments/${selectedEmployee}`);
            } else if (assignmentType === 'role' && selectedRole) {
                ref = doc(db, `companies/${companyId}/role_assignments/${selectedRole}`);
            } else {
                setAssignment({});
                return;
            }
            const snap = await getDoc(ref);
            if (snap.exists()) setAssignment(snap.data()?.permissions || {});
            else setAssignment({});
        } catch (e) {
            setAssignmentMsg('Failed to load assignments');
        }
    };

    // Load subscription data from backoffice path for sync
    const loadSubscriptionData = async (companyId) => {
        try {
            if (!db || !companyId) return;
            
            const subscriptionRef = doc(db, `companies/${companyId}/subscription_details/subscription`);
            const subscriptionSnap = await getDoc(subscriptionRef);
            
            if (subscriptionSnap.exists()) {
                const subscriptionData = subscriptionSnap.data();
                
                // Update selectedPlan with data from subscription_details
                if (subscriptionData.planId) {
                    setSelectedPlan(subscriptionData.planId);
                }
            } else {
            }
        } catch (error) {
            console.error('❌ Error loading subscription data:', error);
        }
    };

    useEffect(() => { loadAssignment(); }, [assignmentType, selectedEmployee, selectedRole, companyId]);

    // Check if employees have joined (have user_companies entry) - HIDDEN FOR FUTURE RELEASE
    const checkEmployeeJoinStatus = async () => {
        if (!employees || employees.length === 0 || !db) return;
        
        console.log('ðŸ” Checking join status for', employees.length, 'employees in company:', companyId);
        console.log('ðŸ“‹ All employees received:', employees.map(e => ({ id: e.id, employeeId: e.employeeId, name: e.name, status: e.status })));
        const joinStatus = {};
        
        for (const employee of employees) {
            console.log('ðŸ‘¤ Checking employee:', employee.name, 'ID:', employee.id, 'UID:', employee.userId || employee.uid);
            
            if (employee.userId || employee.uid) {
                try {
                    // Check if employee has user_companies document
                    const userCompaniesRef = doc(db, 'user_companies', employee.userId || employee.uid);
                    const userCompaniesSnap = await getDoc(userCompaniesRef);
                    
                    if (userCompaniesSnap.exists()) {
                        const userData = userCompaniesSnap.data();
                        console.log('ðŸ“‹ User companies data for', employee.name, ':', userData);
                        
                        // Check if current company is in their companies list
                        const hasCompanyAccess = userData.companies?.some(c => c.companyId === companyId) ||
                                                userData.owned_companies?.some(c => c.companyId === companyId) ||
                                                userData.employed_companies?.some(c => c.companyId === companyId);
                        
                        joinStatus[employee.id] = hasCompanyAccess ? 'joined' : 'not_joined';
                        console.log('âœ… Join status for', employee.name, ':', joinStatus[employee.id]);
                    } else {
                        joinStatus[employee.id] = 'not_joined';
                        console.log('âŒ No user_companies document for', employee.name);
                    }
                } catch (error) {
                    console.error('Error checking join status for employee:', employee.id, error);
                    joinStatus[employee.id] = 'not_joined';
                }
            } else {
                // Employee doesn't have userId/uid, so they haven't joined
                joinStatus[employee.id] = 'not_joined';
                console.log('âš ï¸ Employee', employee.name, 'has no UID - marked as not_joined');
            }
        }
        
        console.log('ðŸ“Š Final join status map:', joinStatus);
        setEmployeeJoinStatus(joinStatus);
    };

    // Check join status when employees or company changes - HIDDEN FOR FUTURE RELEASE
    useEffect(() => {
        if (false && section === 'employee-status') {
            checkEmployeeJoinStatus();
        }
    }, [employees, companyId, section]);

    // Load subscription data
    // Initialize Firebase operations tracking
    const initializeFirebaseTracking = async () => {
        if (!db || !companyId) return;
        
        setFirebaseUsageLoading(true);
        try {
            // Import and initialize usage tracking for this company
            const { initializeCompanyUsage } = await import('../utils/usageTracker');
            await initializeCompanyUsage(db, companyId);
            
            // Initialize auto-tracking Firebase operations
            const { setGlobalCompanyId } = await import('../firebase.config');
            setGlobalCompanyId(companyId);
            
            console.log('✅ Firebase operations tracking initialized for company:', companyId);
        } catch (error) {
            console.error('❌ Error initializing Firebase tracking:', error);
        } finally {
            setFirebaseUsageLoading(false);
        }
    };

    // Initialize Firebase tracking when company changes
    useEffect(() => {
        initializeFirebaseTracking();
    }, [companyId, db]);

    // Handle employee actions from table - HIDDEN FOR FUTURE RELEASE
    const handleEmployeeAction = async (employee, action) => {
        const joinStatus = employeeJoinStatus[employee.id] || 'not_joined';
        const isJoined = joinStatus === 'joined';
        
        const confirmMessages = {
            suspend: `Suspend employee ${employee.name}? They will be temporarily disabled and won't appear in advance/salary tabs. ${isJoined ? 'They also won\'t be able to switch to this company.' : ''}`,
            resume: `Resume employee ${employee.name}? This will restore their access and they will appear in all tabs again.`,
            remove: `âš ï¸ REMOVE employee ${employee.name}?\n\nThis will:\nâ€¢ Remove them from Company Details and Employee Management\nâ€¢ Keep their salary and advance payment history for reports\nâ€¢ They can only be re-added through new employee addition\n\nThis action cannot be undone. Continue?`
        };

        if (!window.confirm(confirmMessages[action])) return;

        try {
            if (action === 'suspend') {
                console.log('ðŸ”„ Suspending employee:', employee.name, 'ID:', employee.id, 'Status before:', employee.status);
                
                // Suspend employee - set status to suspended
                const result = await EmployeeManager.updateEmployee(currentCompany.companyId, employee.id, {
                    status: 'suspended',
                    suspendedAt: serverTimestamp(),
                    suspendedBy: userId
                });
                
                console.log('ðŸ“‹ Suspend result:', result);
                
                if (result.success) {
                    notify.success('Employee suspended successfully! They will not appear in advance/salary tabs and cannot switch to this company.');
                    // Refresh join status
                    await checkEmployeeJoinStatus();
                    
                    // Force reload employees to see the updated status
                    console.log('ðŸ”„ Forcing employee reload...');
                    if (loadCompanyEmployees) {
                        await loadCompanyEmployees(currentCompany.companyId);
                    }
                } else {
                    notify.error('Failed to suspend employee: ' + result.error);
                }
            } else if (action === 'resume') {
                // Resume employee - set status back to active
                const result = await EmployeeManager.updateEmployee(currentCompany.companyId, employee.id, {
                    status: 'active',
                    resumedAt: serverTimestamp(),
                    resumedBy: userId,
                    suspendedAt: null,
                    suspendedBy: null
                });
                
                if (result.success) {
                    notify.success('Employee resumed successfully! They can now access all features and switch to this company.');
                    // Refresh join status
                    await checkEmployeeJoinStatus();
                    
                    // Force reload employees to see the updated status
                    console.log('ðŸ”„ Forcing employee reload after resume...');
                    if (loadCompanyEmployees) {
                        await loadCompanyEmployees(currentCompany.companyId);
                    }
                } else {
                    notify.error('Failed to resume employee: ' + result.error);
                }
            } else if (action === 'remove') {
                // Remove employee from company details and employee management
                // But preserve salary and advance payment history
                const result = await EmployeeManager.updateEmployee(currentCompany.companyId, employee.id, {
                    status: 'removed',
                    removedAt: serverTimestamp(),
                    removedBy: userId,
                    // Mark as removed but don't delete - this preserves salary/advance history
                    isRemoved: true
                });
                
                if (result.success) {
                    notify.success('Employee removed successfully! Their salary and advance payment history has been preserved for reports.');
                    // Refresh join status
                    await checkEmployeeJoinStatus();
                } else {
                    notify.error('Failed to remove employee: ' + result.error);
                }
            }
        } catch (error) {
            console.error('Error performing employee action:', error);
            notify.error('Error: ' + error.message);
        }
    };

    const togglePermission = (category, page, tabKey = null, checked) => {
        setAssignment(prev => {
            const next = { ...(prev || {}) };
            if (!next[category]) next[category] = {};
            if (!next[category][page]) next[category][page] = {};
            if (tabKey === null) {
                next[category][page]._directAccess = !!checked;
            } else {
                next[category][page][tabKey] = !!checked;
            }
            return next;
        });
    };

    const saveAssignment = async () => {
        try {
            if (!db || !companyId) return;
            if (assignmentType === 'employee' && !selectedEmployee) { setAssignmentMsg('Select an employee'); return; }
            if (assignmentType === 'role' && !selectedRole) { setAssignmentMsg('Select a role'); return; }
            let ref;
            if (assignmentType === 'employee') {
                ref = doc(db, `companies/${companyId}/employee_assignments/${selectedEmployee}`);
            } else {
                ref = doc(db, `companies/${companyId}/role_assignments/${selectedRole}`);
            }
            await setDoc(ref, { permissions: assignment, updated_at: serverTimestamp() }, { merge: true });
            setAssignmentMsg('Assignments saved');
        } catch (e) { setAssignmentMsg(e?.message || 'Failed to save'); }
    };



    // Sync section and optional wizard trigger to URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
        if (params.get('section') !== section) params.set('section', section);
        const base = window.location.hash.split('?')[0] || '#/company';
        const next = `${base}?${params.toString()}`;
        if (window.location.hash !== next) window.location.hash = next;
    }, [section]);

    useEffect(() => {
        prevGstinType.current = gstinType;
    }, []);

    const handleGstinTypeChange = (e) => {
        const newType = e.target.value;
        if (prevGstinType.current && prevGstinType.current !== newType) {
            setGstinTypeWarning('Warning: Changing GST Type will affect all future invoices and billing logic.');
        } else {
            setGstinTypeWarning('');
        }
        setGstinType(newType);
        prevGstinType.current = newType;
    };

    const validateGstinState = (gstin, state) => {
        if (!gstin || !state) return '';
        const code = gstin.substring(0, 2);
        const expectedCode = stateCodeMap[state];
        if (expectedCode && code !== expectedCode) {
            return `GSTIN state code (${code}) does not match selected state (${state}, code ${expectedCode}).`;
        }
        return '';
    };

    useEffect(() => {
        if (gstinType === 'Regular' || gstinType === 'Composition') {
            setGstinStateError(validateGstinState(gstin, state));
        } else {
            setGstinStateError('');
        }
    }, [gstin, state, gstinType]);

    // REPLACE handleImageChange with Firebase upload logic and permission handling
    const handleImageChange = async (e, setter, type, previewSetter, setUploading, setProgress) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) { // 3 MB
            notify.warning('File size must be less than 3 MB.', { title: 'File Size Error' });
            return;
        }
        
        // No camera permission needed for file uploads from gallery
        // Permission is only needed when using Camera.getPhoto() for direct camera capture
        
        setUploading(true);
        setProgress(0);
        try {
            // Compress and resize image
            const options = {
                maxSizeMB: 0.08, // 80KB
                maxWidthOrHeight: 200, // px
                useWebWorker: true,
            };
            const compressedFile = await imageCompression(file, options);
            // Show preview from compressed file
            const previewUrl = URL.createObjectURL(compressedFile);
            previewSetter(previewUrl);
            // Upload compressed file to Firebase Storage
            const storage = getStorage();
            // Store under per-user profile path to satisfy storage.rules
            const storageRef = ref(storage, `users/${userId}/profile/${type}_${Date.now()}`);
            const uploadTask = uploadBytesResumable(storageRef, compressedFile);
            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    setProgress(Math.round(progress));
                },
                (error) => {
                    console.error('Upload error:', error);
                    notify.error('Image upload failed. Please try again.', { title: 'Upload Failed', duration: 7000 });
                    setUploading(false);
                    setProgress(0);
                },
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((url) => {
                        setter(url);
                        setUploading(false);
                        setProgress(0);
                        notify.success('Image uploaded successfully!', { title: 'Upload Complete', duration: 6000 });
                    });
                }
            );
        } catch (err) {
            console.error('Image processing error:', err);
            notify.error('Image upload failed. Please try again.', { title: 'Upload Failed', duration: 7000 });
            setUploading(false);
            setProgress(0);
        }
    };

    // Add remove handlers for logo, sign, seal
    const handleRemoveLogo = () => { setLogoUrl(''); setLogoPreview(''); };
    const handleRemoveSign = () => { setSignUrl(''); setSignPreview(''); };
    const handleRemoveSeal = () => { setSealUrl(''); setSealPreview(''); };

    const handleSaveCompanyDetails = async () => {
        if (!db || !userId) {
            notify.warning("Firebase not initialized or user not authenticated.", { title: 'Authentication Error' });
            return;
        }
        if (!firmName) {
            notify.warning("Firm Name is required.", { title: 'Validation Error' });
            return;
        }
        if ((gstinType === 'Regular' || gstinType === 'Composition') && !gstin) {
            notify.warning("GSTIN is required for Regular and Composition GST types.", { title: 'Validation Error' });
            return;
        }
        if (gstinStateError) {
            notify.warning(gstinStateError, { title: 'GSTIN Validation Error' });
            return;
        }
        if (pan && !panRegex.test(pan.toUpperCase())) {
            notify.warning("Invalid PAN. Please enter a valid 10-character PAN as per Indian law (e.g., ABCDE1234F).", { title: 'PAN Validation Error' });
            return;
        }
        try {
            const cid = companyId || currentCompany?.companyId || currentCompany?.id;
            if (!cid) { notify.warning('No company selected', { title: 'Validation Error' }); return; }
            const companyData = {
                firmName,
                gstin: (gstinType === 'Regular' || gstinType === 'Composition') ? gstin : '',
                address,
                city,
                state,
                pincode,
                contactNumber,
                email,
                pan: pan ? pan.toUpperCase() : '',
                gstinType,
                bankName,
                bankAccount,
                bankIfsc,
                logoUrl,
                signUrl,
                sealUrl,
                upiId,
                upiQrUrl,
                paymentGatewayLink,
                salesTerms,
                purchaseTerms,
                footer,
                quotationTerms,
                companyType,
                companyTypeOther
            };
            await saveCompanyDetails(db, { companyId: cid, appId, userId }, companyData, { mirrorLegacy: false });
            notify.success('Company details saved successfully!', { 
                title: 'Company Updated',
                details: `${firmName} • ${gstin || 'No GSTIN'}`,
                duration: 6000 
            });
        } catch (err) {
            console.error('Error saving company details:', err);
            notify.error('Error saving company details. Please try again.', { title: 'Save Failed', duration: 7000 });
        }
    };

    // Save Payment Gateway Settings
    const savePayments = async () => {
        try {
            if (!db || !companyId) return;
            const paymentData = {
                provider: pg.provider,
                mode: pg.mode,
                keyId: pg.keyId,
                updatedAt: new Date()
            };
            await setDoc(doc(db, `companies/${companyId}/settings/payment_gateway`), paymentData);
            setPgMsg('Payment gateway settings saved successfully!');
            setTimeout(() => setPgMsg(''), 3000);
        } catch (error) {
            console.error('Error saving payment settings:', error);
            setPgMsg('Error saving payment settings. Please try again.');
        }
    };

    // Save SMS Settings
    const saveSMS = async () => {
        try {
            if (!db || !companyId) return;
            const smsData = {
                provider: sms.provider,
                senderId: sms.senderId,
                defaultTemplate: sms.defaultTemplate,
                updatedAt: new Date()
            };
            await setDoc(doc(db, `companies/${companyId}/settings/sms`), smsData);
            setSmsMsg('SMS settings saved successfully!');
            setTimeout(() => setSmsMsg(''), 3000);
        } catch (error) {
            console.error('Error saving SMS settings:', error);
            setSmsMsg('Error saving SMS settings. Please try again.');
        }
    };

    // Save Branding Settings
    const saveBranding = async () => {
        try {
            if (!db || !companyId) return;
            const brandingData = {
                shareMessageDefault: brand.shareMessageDefault,
                footerText: brand.footerText,
                updatedAt: new Date()
            };
            await setDoc(doc(db, `companies/${companyId}/settings/branding`), brandingData);
            setBrandMsg('Branding settings saved successfully!');
            setTimeout(() => setBrandMsg(''), 3000);
        } catch (error) {
            console.error('Error saving branding settings:', error);
            setBrandMsg('Error saving branding settings. Please try again.');
        }
    };


    return (
        <div className="min-h-screen bg-gray-50">
            {/* Company Wizard - Only show when explicitly requested */}
            {showWizard && (
            <CompanyWizard
                isOpen={showWizard}
                onClose={() => setShowWizard(false)}
                mode={wizardMode}
                existingData={wizardData}
                onSubmit={handleWizardComplete}
                startStep={wizardStartStep}
            />
            )}

            {/* Delete Company Modal */}
            <DeleteCompanyModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                companyName={firmName || 'this company'}
                companyId={companyId}
                onConfirmDelete={handleDeleteCompany}
            />

            {/* Main Company Details Page - Show when wizard is not open */}
            {!showWizard && (
                <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-white min-h-screen">
                    {/* Modern Header */}
                    <div className="mb-8">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <div>
                                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                                    Company Details
                                </h1>
                                <p className="text-gray-600">Manage your company information and settings</p>
                            </div>
                            <button
                                onClick={() => openWizard({
                                    firmName, gstin, address, city, state, pincode,
                                    contactNumber, email, pan
                                }, 'edit')}
                                className="group flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                            >
                                <svg className="w-5 h-5 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                <span>Edit with Wizard</span>
                            </button>
                        </div>

                        {/* Tab Navigation - Responsive */}
                        <div className="border-b border-gray-200 mb-6">
                            {/* Desktop Tabs */}
                            <nav className="hidden md:flex -mb-px space-x-8">
                            <button
                                onClick={() => setActiveTab('profile')}
                                className={`${activeTab === 'profile' 
                                    ? 'border-blue-500 text-blue-600' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                            >
                                Company Profile
                            </button>
                            <button
                                onClick={() => setActiveTab('payments')}
                                className={`${activeTab === 'payments' 
                                    ? 'border-blue-500 text-blue-600' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                            >
                                💳 Payments
                            </button>
                            <button
                                onClick={() => setActiveTab('sms')}
                                className={`${activeTab === 'sms' 
                                    ? 'border-blue-500 text-blue-600' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                            >
                                📱 SMS
                            </button>
                            <button
                                onClick={() => setActiveTab('branding')}
                                className={`${activeTab === 'branding' 
                                    ? 'border-blue-500 text-blue-600' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                            >
                                🎨 Branding
                            </button>
                            <button
                                onClick={() => setActiveTab('currency')}
                                className={`${activeTab === 'currency' 
                                    ? 'border-blue-500 text-blue-600' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                            >
                                💰 Currency
                            </button>
                            <button
                                onClick={() => setActiveTab('subscription')}
                                className={`${activeTab === 'subscription' 
                                    ? 'border-blue-500 text-blue-600' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                            >
                                📋 Subscription
                            </button>
                        </nav>

                        {/* Mobile Dropdown */}
                        <div className="md:hidden">
                            <AppSelect
                                value={activeTab}
                                onChange={(e) => setActiveTab(e.target.value)}
                                aria-label="Company details section"
                                sheetTitle="Company details section"
                                options={[
                                    { value: 'profile', label: 'Company Profile' },
                                    { value: 'payments', label: '💳 Payments' },
                                    { value: 'sms', label: '📱 SMS' },
                                    { value: 'branding', label: '🎨 Branding' },
                                    { value: 'currency', label: '💰 Currency' },
                                    { value: 'subscription', label: '📋 Subscription' },
                                ]}
                                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} shadow-sm focus:border-blue-500 bg-white`}
                            />
                            </div>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'profile' && (
                        <div className="space-y-6">
                            {/* Modern Section Cards */}
                            {/* Tiles Grid - responsive 1/2 columns for better spacing */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Basic Info Card */}
                                <ModernInfoCard
                                    title="Basic Information"
                                    icon={Building2}
                                    gradient="from-blue-500 to-cyan-600"
                                    onEdit={() => openWizard({ firmName, gstin, pan, gstinType, companyType, contactPerson, businessType, email, contactNumber }, 'edit', 0)}
                                >
                                    <div className="space-y-3">
                                        <InfoRow icon={Building2} label="Company Name" value={firmName} highlight />
                                        <InfoRow icon={Hash} label="Company ID" value={companyId || currentCompany?.companyId || currentCompany?.id || 'N/A'} />
                                        <InfoRow label="Company Type" value={companyType} />
                                        <InfoRow label="Business Type" value={businessType} />
                                        <InfoRow icon={Award} label="GST Type" value={gstinType} />
                                        <InfoRow icon={Receipt} label="GSTIN" value={gstin || (gstinType === 'Unregistered' ? 'N/A' : null)} />
                                        <InfoRow icon={CreditCard} label="PAN" value={pan} />
                                        <InfoRow icon={Users} label="Contact Person" value={contactPerson} />
                                        <InfoRow icon={Mail} label="Email" value={email} />
                                        <InfoRow icon={PhoneIcon} label="Phone" value={contactNumber} />
                                    </div>
                                </ModernInfoCard>

                                {/* Address Card */}
                                <ModernInfoCard
                                    title="Location & Address"
                                    icon={MapPin}
                                    gradient="from-indigo-500 to-purple-600"
                                    onEdit={() => openWizard({ address, city, state, pincode }, 'edit', 1)}
                                >
                                    <div className="space-y-3">
                                        <InfoRow icon={MapPin} label="Street Address" value={address} />
                                        <InfoRow icon={Globe} label="City" value={city} />
                                        <InfoRow label="State" value={state} />
                                        <InfoRow label="PIN Code" value={pincode} />
                                        {(city || state || pincode) && (
                                            <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                                                <div className="text-xs font-medium text-indigo-800 mb-1">Full Address</div>
                                                <div className="text-sm text-indigo-900">
                                                    {[address, city, state, pincode].filter(Boolean).join(', ')}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </ModernInfoCard>


                                {/* Bank Details Card */}
                                <ModernInfoCard
                                    title="Banking & Payments"
                                    icon={Landmark}
                                    gradient="from-green-500 to-emerald-600"
                                    onEdit={() => openWizard({ bankName, bankAccount, bankIfsc, upiId, upiQrUrl }, 'edit', 2)}
                                >
                                    <div className="space-y-3">
                                        <InfoRow icon={Landmark} label="Bank Name" value={bankName} />
                                        <InfoRow icon={CreditCard} label="Account Number" value={bankAccount} />
                                        <InfoRow label="IFSC Code" value={bankIfsc} />
                                        <InfoRow icon={PhoneIcon} label="UPI ID" value={upiId} />
                                        {upiQrUrl && (
                                            <div className="mt-4 pt-4 border-t border-gray-100">
                                                <div className="flex items-center justify-center">
                                                    <div className="p-3 bg-white rounded-xl border-2 border-green-200 shadow-sm">
                                                        <img 
                                                            src={upiQrUrl} 
                                                            alt="UPI QR Code" 
                                                            className="w-24 h-24 object-contain"
                                                        />
                                                        <div className="text-xs text-center mt-2 text-gray-500 font-medium">UPI QR Code</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </ModernInfoCard>

                                {/* Documents Card */}
                                <ModernInfoCard
                                    title="Documents & Assets"
                                    icon={FileTextIcon}
                                    gradient="from-purple-500 to-pink-600"
                                    onEdit={() => openWizard({ logoUrl, sealUrl, signUrl, upiQrUrl }, 'edit', 3)}
                                >
                                    <div className="grid grid-cols-3 gap-4">
                                        <ImagePreview 
                                            src={logoUrl}
                                            alt="Company Logo"
                                            label="Logo"
                                        />
                                        <ImagePreview 
                                            src={sealUrl}
                                            alt="Company Seal"
                                            label="Seal"
                                        />
                                        <ImagePreview 
                                            src={signUrl}
                                            alt="Signature"
                                            label="Signature"
                                        />
                                    </div>
                                </ModernInfoCard>

                                {/* Settings Card */}
                                <ModernInfoCard
                                    title="Preferences & Settings"
                                    icon={SettingsIcon}
                                    gradient="from-orange-500 to-red-600"
                                    onEdit={() => openWizard({ currency, gstCalculation, selectedPlan, gstinType }, 'edit', 4)}
                                >
                                    <div className="space-y-3">
                                        <InfoRow label="Currency" value={currency || 'INR'} />
                                        <InfoRow label="GST Calculation" value={gstCalculation || 'excluding'} />
                                        <InfoRow label="Subscription Plan" value={selectedPlan || 'Not selected'} highlight />
                                    </div>
                                </ModernInfoCard>
                                            
                                {/* Terms & Conditions Card */}
                                <ModernInfoCard
                                    title="Terms & Conditions"
                                    icon={FileTextIcon}
                                    gradient="from-teal-500 to-green-600"
                                    onEdit={() => openWizard({ salesTerms, purchaseTerms, quotationTerms, footer }, 'edit', 6)}
                                >
                                    <div className="space-y-2">
                                        {/* Terms Status Grid */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className={`p-3 rounded-lg text-center border-2 ${salesTerms ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                                <div className="text-xs text-gray-600 mb-1">Sales Terms</div>
                                                <div className={`text-sm font-semibold ${salesTerms ? 'text-green-700' : 'text-gray-400'}`}>
                                                    {salesTerms ? '✓ Set' : 'Not Set'}
                                                </div>
                                            </div>
                                            <div className={`p-3 rounded-lg text-center border-2 ${purchaseTerms ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                                <div className="text-xs text-gray-600 mb-1">Purchase Terms</div>
                                                <div className={`text-sm font-semibold ${purchaseTerms ? 'text-green-700' : 'text-gray-400'}`}>
                                                    {purchaseTerms ? '✓ Set' : 'Not Set'}
                                                </div>
                                            </div>
                                            <div className={`p-3 rounded-lg text-center border-2 ${quotationTerms ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                                <div className="text-xs text-gray-600 mb-1">Quotation Terms</div>
                                                <div className={`text-sm font-semibold ${quotationTerms ? 'text-green-700' : 'text-gray-400'}`}>
                                                    {quotationTerms ? '✓ Set' : 'Not Set'}
                                                </div>
                                            </div>
                                            <div className={`p-3 rounded-lg text-center border-2 ${footer ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                                                <div className="text-xs text-gray-600 mb-1">Footer</div>
                                                <div className={`text-sm font-semibold ${footer ? 'text-green-700' : 'text-gray-400'}`}>
                                                    {footer ? '✓ Set' : 'Not Set'}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Expandable Details (keep old functionality) */}
                                        <details className="mt-3">
                                            <summary className="cursor-pointer text-xs font-medium text-teal-700 hover:text-teal-800 py-2">
                                                Click to view/edit individual terms
                                            </summary>
                                            <div className="mt-3 space-y-3 border-t pt-3">
                                            {/* Sales Terms - Collapsed */}
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs font-medium">Sales Terms</span>
                                                    {salesTerms && (
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleTermsDropdown('sales');
                                                            }}
                                                            className="text-xs px-2 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 flex items-center gap-1"
                                                        >
                                                            View
                                                            <svg className={`w-3 h-3 transition-transform ${activeTermsDropdown === 'sales' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                                {activeTermsDropdown === 'sales' && salesTerms && (
                                                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-2">
                                                        <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">
                                                            {salesTerms}
                                                        </div>
                                                        <div className="mt-3 pt-2 border-t border-gray-200">
                                                            <button 
                                                                onClick={handleTermsEdit}
                                                                className="text-xs px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700"
                                                            >
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Purchase Terms */}
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium">Purchase: {purchaseTerms ? 'Configured' : '—'}</span>
                                                    {purchaseTerms && (
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleTermsDropdown('purchase');
                                                            }}
                                                            className="text-xs px-2 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 flex items-center gap-1"
                                                        >
                                                            View
                                                            <svg className={`w-3 h-3 transition-transform ${activeTermsDropdown === 'purchase' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                                {activeTermsDropdown === 'purchase' && purchaseTerms && (
                                                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-2">
                                                        <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">
                                                            {purchaseTerms}
                                                        </div>
                                                        <div className="mt-3 pt-2 border-t border-gray-200">
                                                            <button 
                                                                onClick={handleTermsEdit}
                                                                className="text-xs px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700"
                                                            >
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Quotation Terms */}
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium">Quotation: {quotationTerms ? 'Configured' : '—'}</span>
                                                    {quotationTerms && (
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleTermsDropdown('quotation');
                                                            }}
                                                            className="text-xs px-2 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 flex items-center gap-1"
                                                        >
                                                            View
                                                            <svg className={`w-3 h-3 transition-transform ${activeTermsDropdown === 'quotation' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                                {activeTermsDropdown === 'quotation' && quotationTerms && (
                                                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-2">
                                                        <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">
                                                            {quotationTerms}
                                                        </div>
                                                        <div className="mt-3 pt-2 border-t border-gray-200">
                                                            <button 
                                                                onClick={handleTermsEdit}
                                                                className="text-xs px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700"
                                                            >
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Footer */}
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="font-medium">Footer: {footer ? 'Configured' : '—'}</span>
                                                    {footer && (
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleTermsDropdown('footer');
                                                            }}
                                                            className="text-xs px-2 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 flex items-center gap-1"
                                                        >
                                                            View
                                                            <svg className={`w-3 h-3 transition-transform ${activeTermsDropdown === 'footer' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                                {activeTermsDropdown === 'footer' && footer && (
                                                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mb-2">
                                                        <div className="text-xs text-gray-700 whitespace-pre-line leading-relaxed">
                                                            {footer}
                                                        </div>
                                                        <div className="mt-3 pt-2 border-t border-gray-200">
                                                            <button 
                                                                onClick={handleTermsEdit}
                                                                className="text-xs px-3 py-1 rounded bg-teal-600 text-white hover:bg-teal-700"
                                                            >
                                                                Edit
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                            </div>
                                        </details>
                                    </div>
                                </ModernInfoCard>

                            </div>
                            
                            {/* Mobile: Dropdown */}
                            <div className="md:hidden">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Select Section to Edit:</label>
                                <AppSelect
                                    value=""
                                    onChange={(e) => {
                                        const stepIndex = parseInt(e.target.value);
                                        if (stepIndex >= 0) {
                                            const stepData = {
                                                0: { firmName, gstin, pan, gstinType, companyType, contactPerson, businessType },
                                                1: { address, city, state, pincode },
                                                2: { bankName, bankAccount, bankIfsc, upiId },
                                                3: { logoUrl, sealUrl, signUrl, upiQrUrl },
                                                4: {},
                                                6: { salesTerms, purchaseTerms, quotationTerms, footer }
                                            };
                                            openWizard(stepData[stepIndex] || {}, 'edit', stepIndex);
                                        }
                                    }}
                                    placeholder="Choose section to edit..."
                                    aria-label="Select section to edit"
                                    sheetTitle="Select section to edit"
                                    options={[
                                        { value: '0', label: '📝 Basic Information' },
                                        { value: '1', label: '📍 Address Details' },
                                        { value: '2', label: '🏦 Bank Details' },
                                        { value: '3', label: '📄 Documents & Images' },
                                        { value: '4', label: '⚙️ Company Settings' },
                                        { value: '6', label: '📋 Terms & Conditions' },
                                    ]}
                                    triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
                                />
                            </div>

                            {/* Quick Actions */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                                <QuickActionButton 
                                    icon={Receipt}
                                    label="Tax Reports"
                                    onClick={() => navigate('/taxes')}
                                    color="purple"
                                />
                                <QuickActionButton 
                                    icon={Users}
                                    label="Manage Team"
                                    onClick={() => navigate('/employee-management')}
                                    color="green"
                                />
                            </div>

                            {/* Danger Zone */}
                            {canDelete && (
                                <div className="mt-8 pt-6 border-t-2 border-red-200">
                                    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
                                        <div className="flex items-start gap-3 mb-4">
                                            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            <div className="flex-1">
                                                <h4 className="text-lg font-bold text-red-900 mb-1">⚠️ Danger Zone</h4>
                                                <p className="text-sm text-red-700 mb-3">
                                                    Deleting your company will permanently remove all data. This action cannot be undone.
                                                </p>
                                                <button
                                                    onClick={() => setShowDeleteModal(true)}
                                                    className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-lg shadow-md transition duration-200 flex items-center gap-2"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    Delete Company
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        {activeTab === 'payments' && (
                            <div className="space-y-6">
                                <h3 className="text-lg font-semibold text-gray-800">Payment Integration</h3>
                                <p className="text-gray-600">Configure payment gateways, UPI, and bank details for your business.</p>
                                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                    <p className="text-yellow-800">Payment integration features coming soon...</p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'sms' && (
                            <div className="space-y-6">
                                <h3 className="text-lg font-semibold text-gray-800">SMS Integration</h3>
                                <p className="text-gray-600">Set up SMS notifications for invoices, payments, and customer communications.</p>
                                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                    <p className="text-yellow-800">SMS integration features coming soon...</p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'branding' && (
                            <div className="space-y-6">
                                <h3 className="text-lg font-semibold text-gray-800">Company Branding</h3>
                                <p className="text-gray-600">Upload company logo, letterhead, and customize invoice templates.</p>
                                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                    <p className="text-yellow-800">Branding features coming soon...</p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'currency' && (
                            <CurrencySettings />
                        )}

                        {activeTab === 'subscription' && (
                            <div className="space-y-6">
                                <h3 className="text-lg font-semibold text-gray-800">Firebase Operations Usage</h3>
                                <p className="text-gray-600">Monitor your Firebase operations and storage usage with real-time tracking.</p>
                                
                                {/* Loading State */}
                                {firebaseUsageLoading && (
                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                        <div className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span className="text-blue-800">Initializing Firebase operations tracking...</span>
                                        </div>
                                    </div>
                                )}

                                {/* Firebase Operations Usage Monitor */}
                                <UsageMonitor 
                                    companyId={currentCompany?.companyId || currentCompany} 
                                    showHeader={true}
                                    refreshTrigger={refreshTrigger}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyDetails; 
